import React from "react";
import { InputAdornment, TextField, TextFieldProps } from "@mui/material";
import styled from "styled-components";
import { colors, fonts } from "../../components/common/mixins";
import { Check } from "../../components/icons/Check";

type DsTextFieldProps = TextFieldProps & { showCheckOnDisabled?: boolean };

export const DsTextField = styled(
  React.forwardRef<HTMLDivElement, DsTextFieldProps>(({ showCheckOnDisabled = true, ...props }, ref) => {
    const inputRef = React.useRef<HTMLInputElement>(null);
    const [hasValue, setHasValue] = React.useState(!!props.value || !!props.defaultValue);

    React.useEffect(() => {
      if (inputRef.current) {
        setHasValue(!!inputRef.current.value);
      }
    });

    const showCheckIcon = showCheckOnDisabled && !!props.disabled && hasValue;

    return (
      <TextField
        {...props as TextFieldProps}
        ref={ref}
        inputRef={inputRef}
        variant="outlined"
        inputProps={{
          ...props.inputProps,
          onDrop: (e: React.DragEvent) => e.preventDefault(),
        }}
        InputProps={{
          ...props.InputProps,
          ...(showCheckIcon && {
            endAdornment: (
              <InputAdornment position="end">
                <Check />
              </InputAdornment>
            ),
          }),
        }}
      />
    );
  }),
)`
  width: 100%;

  /* ================= LABEL ================= */

  & .MuiInputLabel-root {
    ${fonts.normalText()};
    color: ${colors.formDefault};
  }

  & .MuiInputLabel-root.Mui-disabled {
    color: ${colors.formDefault};
  }

  & .MuiFormLabel-asterisk {
    color: ${colors.danger};
  }

  & .MuiInputLabel-root.Mui-focused {
    color: ${colors.primary};
  }

  & .MuiInputLabel-root.Mui-focused:has(~ * input[readonly]),
  &:has(input[readonly]) .MuiInputLabel-root.Mui-focused 
  {
    color: ${colors.formDefault};
  }

  /* ERROR / WARNING label */
  & .MuiInputLabel-root.Mui-error {
    color: ${colors.danger};
  }

  & .MuiInputLabel-outlined {
    transform: translate(14px, 12px);
  }

  & .MuiInputLabel-outlined.MuiInputLabel-shrink {
    transform: translate(14px, -9px) scale(0.75);
  }

  @media (max-width: 900px) {
    & .MuiInputLabel-outlined {
      transform: translate(14px, 10.5px);
    }
  }

  /* ================= INPUT ================= */

  & .MuiOutlinedInput-root {
    ${fonts.normalText()};

    @media (max-width: 900px) {
      min-height: 40px;
      & .MuiOutlinedInput-input {
        padding-top: 9px;
        padding-bottom: 9px;
        font-size: 16px !important; /* prevents iOS auto-zoom on focus */
      }
      &.MuiAutocomplete-inputRoot {
        min-height: 40px;
        & .MuiAutocomplete-input {
          padding-top: 0;
          padding-bottom: 0;
        }
      }
    }

    & input::placeholder,
    & .MuiOutlinedInput-input::placeholder {
      color: ${colors.gray};
      opacity: 1;
    }

    &.Mui-disabled {
      background-color: ${colors.formDisabledBg};
    }

    &.Mui-disabled .MuiOutlinedInput-input {
      -webkit-text-fill-color: ${colors.formDefault}; /* for Safari */
      color: ${colors.formDefault};
    }

    &.Mui-disabled .MuiInputAdornment-root,
    &.Mui-disabled .MuiInputAdornment-root * {
      color: ${colors.formDefault};
    }

    /* border default */
    & fieldset {
      border-color: ${colors.formDefault};
    }

    /* suppress MUI's border-color transition on touch devices to prevent flicker during scroll */
    @media (hover: none) {
      & fieldset {
        transition: none;
      }

      /* override MUI's sticky :hover border (rgba(0,0,0,0.23)) after touch/long-press on iOS */
      &:hover fieldset {
        border-color: ${colors.formDefault};
      }
    }

    /* border focused */
    &.Mui-focused fieldset {
      border-color: ${colors.primary};
      border-width: 1px;
    }

    /* border focused readonly — keep default color and width */
    &.Mui-focused:has(input[readonly]) fieldset {
      border-color: ${colors.formDefault};
      border-width: 1px;
    }

    /* ERROR / WARNING border */
    &.Mui-error fieldset {
      border-color: ${colors.danger};
    }

    /* border disabled */
    &.Mui-disabled fieldset {
     border-color: ${colors.lightGray};
    }

    /* remove extra right padding when lock adornment is shown */
    &.Mui-disabled.MuiInputBase-adornedEnd {
      padding-right: 12px;
    }
  }

  /* ================= HELPER TEXT ================= */

  & .MuiFormHelperText-root {
    ${fonts.normalTextExtraSmall()};
    margin-left: 0;
    margin-right: 0;
  }

  & .MuiFormHelperText-root.Mui-error {
    color: ${colors.danger};
  }

  /* ================= NOTCH ================= */

  /* notch span matches the padded label */
  & .MuiOutlinedInput-notchedOutline legend > span {
    padding-left: 6px;
    padding-right: 6px;
  }
`;
